import { Helmet } from 'react-helmet-async';
import { useSiteSettings } from '@/hooks/useSiteSettings';
import { useLlmMetadata } from '@/hooks/useLlmMetadata';
import { buildBaseJsonLd } from '@/lib/structuredData';

interface SEOHeadProps {
  /** Page-specific title (will be combined with site name as "Title | Site Name") */
  title?: string;
  /** Page-specific description - falls back to global SEO description */
  description?: string;
  /** Current path for canonical URL (e.g., "/gallery") */
  path?: string;
  /** Custom OG image - falls back to global OG image */
  image?: string;
  /** Page type for Open Graph */
  type?: 'website' | 'article';
  /** Set to true to prevent indexing (for admin/auth pages) */
  noIndex?: boolean;
  /** Keywords override - falls back to global keywords */
  keywords?: string;

  /** Optional page-specific JSON-LD. Can be a single object or array of objects. */
  structuredData?: Record<string, unknown> | Array<Record<string, unknown>>;
}

/**
 * SEOHead component for managing page-specific meta tags
 * Uses react-helmet-async to dynamically update document head
 */
export const SEOHead = ({
  title,
  description,
  path = '',
  image,
  type = 'website',
  noIndex = false,
  keywords,
  structuredData,
}: SEOHeadProps) => {
  const { settings } = useSiteSettings();
  const { settings: llmSettings, isJsonLdEnabled } = useLlmMetadata();

  // Build full title: "Page Title | Site Name" or default SEO title
  const siteName = settings.site_name || 'CineRig Pro';
  const fullTitle = title ? `${title} | ${siteName}` : settings.seo_title;

  // Use provided values or fall back to global settings
  const metaDescription = description || settings.seo_description;
  const metaKeywords = keywords || settings.seo_keywords;
  const ogImage = image || settings.seo_og_image || 'https://jcptwvlqvumfedrtvdfr.supabase.co/storage/v1/object/public/gallery/branding/og-image.png';
  const canonicalDomain = settings.canonical_domain || 'https://cinerig.pro';
  const canonicalUrl = `${canonicalDomain}${path}`;
  const twitterHandle = settings.seo_twitter_handle || '@CineRigPro';

  const effectiveDescription = (isJsonLdEnabled && llmSettings.app_description) ? llmSettings.app_description : metaDescription;
  const effectiveAppName = (isJsonLdEnabled && llmSettings.app_name) ? llmSettings.app_name : siteName;
  const effectiveCategory = (isJsonLdEnabled && llmSettings.app_category) ? llmSettings.app_category : undefined;
  const effectiveFeatureList = (isJsonLdEnabled && Array.isArray(llmSettings.features)) ? llmSettings.features : undefined;

  const baseJsonLd = isJsonLdEnabled
    ? buildBaseJsonLd({
        siteName,
        canonicalDomain,
        canonicalUrl,
        description: effectiveDescription,
        ogImage,
        appName: effectiveAppName,
        appCategory: effectiveCategory,
        featureList: effectiveFeatureList,
      })
    : [];

  const pageJsonLdArray = structuredData
    ? Array.isArray(structuredData)
      ? structuredData
      : [structuredData]
    : [];

  const jsonLdBlocks = [...baseJsonLd, ...pageJsonLdArray];

  return (
    <Helmet>
      {/* Favicon is set statically in index.html to avoid post-mount swap/flash */}
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={metaKeywords} />
      <meta name="author" content={siteName} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content={siteName} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={twitterHandle} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={ogImage} />

      {/* Robots */}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Structured Data (JSON-LD) */}
      {jsonLdBlocks.map((block, idx) => (
        <script
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(block) }}
          key={`jsonld-${idx}`}
          type="application/ld+json"
        />
      ))}
    </Helmet>
  );
};

export default SEOHead;
